import React from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Typography
} from '@mui/material';
import MeasurementImageButton from './MeasurementImageButton';

import type { Measurement } from '../../hooks/useExecutionStatus';

type Props = {
  measurements: Measurement[];
  scriptId?: string;
  runId?: string;
};

const RESULT_COLORS: Record<string, 'success' | 'error' | 'default'> = {
  PASS: 'success',
  FAIL: 'error',
  PENDING: 'default'
};

const formatValue = (val: number | null | undefined) => {
  if (val == null) return '—';
  return String(val);
};

const MeasurementsTab: React.FC<Props> = ({
  measurements,
  scriptId,
  runId
}) => {
  if (measurements.length === 0) {
    return (
      <Box sx={{ p: 2, textAlign: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          No measurements recorded yet
        </Typography>
      </Box>
    );
  }

  return (
    <TableContainer sx={{ height: '100%' }}>
      <Table size="small" stickyHeader>
        <TableHead>
          <TableRow>
            <TableCell>Test Point</TableCell>
            <TableCell align="right">Nominal</TableCell>
            <TableCell align="right">Measured</TableCell>
            <TableCell>Unit</TableCell>
            <TableCell align="right">Error</TableCell>
            <TableCell align="center">Result</TableCell>
            <TableCell align="center">Image</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {measurements.map(m => (
            <TableRow key={m.testPoint}>
              <TableCell>{m.testPoint}</TableCell>
              <TableCell align="right" sx={{ fontFamily: 'monospace' }}>
                {formatValue(m.nominal)}
              </TableCell>
              <TableCell align="right" sx={{ fontFamily: 'monospace' }}>
                {m.measuredText ?? formatValue(m.measured)}
              </TableCell>
              <TableCell>{m.unit ?? '—'}</TableCell>
              <TableCell align="right" sx={{ fontFamily: 'monospace' }}>
                {formatValue(m.error)}
              </TableCell>
              <TableCell align="center">
                {m.passFail ? (
                  <Chip
                    label={m.passFail}
                    size="small"
                    color={RESULT_COLORS[m.passFail] ?? 'default'}
                    sx={{ height: 20, fontSize: '0.65rem' }}
                  />
                ) : (
                  <Typography variant="caption" color="text.secondary">
                    —
                  </Typography>
                )}
              </TableCell>
              <TableCell align="center">
                {m.imageId && scriptId && runId ? (
                  <MeasurementImageButton
                    imageId={m.imageId}
                    scriptId={scriptId}
                    runId={runId}
                  />
                ) : null}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default MeasurementsTab;
